import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { hideModal } from '../../managers/modal'
import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, optional, required, requiredEmail } from 'utils/validations'
import { string, StringSchema } from 'yup'
import { UIButton } from 'components/ui/Button'
import { TextArea } from 'components/ui/TextArea'
import { openTicket, useGetTicketAdditionalFields } from 'api/tickets'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { settingsSelectors } from 'store/settings'
import { showSuccessToast } from 'utils/toast'
import { AdditionalFieldTypeEnum, TicketAdditionalField } from 'types/ticket-additional-field'
import { InputRadio } from 'components/ui/InputRadio'
import { clsx } from 'clsx'
import { Transition } from '@headlessui/react'
import { useGetSetting } from 'api/settings'
import { ScrollDiv } from 'components/modals/OpenChatModal'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { LanguagesEnum } from 'types/index'

export const OpenTicketModal = () => {
  const { t } = useTranslation()
  const isLogged = useSelector(settingsSelectors.isLogged)
  const lang = useSelector(settingsSelectors.lang)
  const { data: ticketAdditionalFields } = useGetTicketAdditionalFields()
  const [loading, setLoading] = useState(false)
  const { isAppMode } = useGetBotMode()
  const { setting } = useGetSetting()

  const additionalFieldsInitialValues = useMemo(() => {
    return (
      ticketAdditionalFields?.reduce((acc: { [key: string]: string }, field: TicketAdditionalField) => {
        acc[field.name] = ''
        return acc
      }, {}) || {}
    )
  }, [ticketAdditionalFields])

  const additionalFieldsValidationSchema = useMemo(() => {
    return (
      ticketAdditionalFields?.reduce((acc: { [key: string]: StringSchema<any> }, field: TicketAdditionalField) => {
        acc[field.name] = field.isRequired ? required : optional
        return acc
      }, {}) || {}
    )
  }, [ticketAdditionalFields])

  const validationSchema = (isLogged: boolean) =>
    createValidationSchema({
      ...additionalFieldsValidationSchema,
      email: isLogged ? string() : requiredEmail,
      name: required,
      surname: required,
      agency: optional,
      languageArea: required,
      description: required,
    })

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik({
    initialValues: {
      ...additionalFieldsInitialValues,
      email: '',
      name: '',
      surname: '',
      agency: '',
      languageArea: lang || LanguagesEnum.en,
      description: '',
    },
    validationSchema: validationSchema(isLogged),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        setLoading(true)
        await openTicket({
          email: values.email,
          name: values.name,
          surname: values.surname,
          agency: values.agency,
          languageArea: values.languageArea,
          description: values.description,
          additionalTicketInfo: ticketAdditionalFields?.reduce(
            (acc: { [key: string]: string }, field: TicketAdditionalField) => {
              acc[field.name] = values[field.name as keyof typeof values]
              return acc
            },
            {},
          ),
        })
        setLoading(false)
        showSuccessToast(t('ticketOpenedSuccess'))
        hideModal()
      } catch (e) {
        console.error({ e })
        setLoading(false)
      }
    },
  })

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  return (
    <div style={{ padding: 20 }}>
      <div className={'tw-flex tw-flex-row tw-items-center tw-justify-between'}>
        <div className={'tw-font-[800]'}>{t('needToOpenTicket')}</div>
        <div className={'tw-cursor-pointer'}>
          <FontAwesomeIcon icon={['far', 'times']} onClick={hideModal} />
        </div>
      </div>
      <div
        className={clsx(
          'tw-flex tw-flex-col tw-space-y-1 tw-mt-2',
          isAppMode ? 'modal-container-app' : `modal-container`,
        )}
      >
        <ScrollDiv
          widgetColor={setting?.secondaryColor}
          className={'tw-flex tw-flex-col tw-overflow-y-auto tw-px-2 tw-gap-y-2'}
        >
          {!isLogged && <Input label={t('email')} {...fieldProps('email')} />}
          <Input label={t('name')} {...fieldProps('name')} />
          <Input label={t('surname')} {...fieldProps('surname')} />
          <Input label={t('agency')} {...fieldProps('agency')} />
          {Object.values(LanguagesEnum).map((lang) => {
            return (
              <InputRadio
                key={lang}
                value={lang}
                label={t(lang)}
                checked={values.languageArea === lang}
                onChange={(e) => {
                  setFieldValue('languageArea', e)
                  setFieldError('languageArea', '')
                }}
              />
            )
          })}
          <TextArea label={t('description')} {...fieldProps('description')} />
          {ticketAdditionalFields?.map((field: TicketAdditionalField) => {
            if (field.additionalFieldType === AdditionalFieldTypeEnum.FREE) {
              return (
                <Input
                  type={'text'}
                  key={field.name}
                  label={field.name}
                  {...fieldProps(field.name as keyof typeof values)}
                />
              )
            }
            if (field.additionalFieldType === AdditionalFieldTypeEnum.SELECT) {
              return (
                <TicketAdditionalFieldSelect
                  key={field.name}
                  field={field}
                  error={errors[field.name as keyof typeof values]}
                  values={values}
                  onChange={(e) => {
                    setFieldValue(field.name, e)
                    setFieldError(field.name, '')
                  }}
                />
              )
            }
            return null
          })}
        </ScrollDiv>
      </div>
      <div className={'tw-mt-6 tw-w-full'}>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={loading}>
          <span className={'tw-text-sm'}>{t('confirm')}</span>
        </UIButton>
      </div>
    </div>
  )
}

type TicketAdditionalFieldSelectProps = {
  field: TicketAdditionalField
  error?: string
  values: any
  onChange: (e: any) => void
}
export const TicketAdditionalFieldSelect = ({ field, error, values, onChange }: TicketAdditionalFieldSelectProps) => {
  const { t } = useTranslation()
  return (
    <div key={field.name}>
      <div className={'tw-text-white tw-text-sm tw-mb-1'}>{field.name}</div>
      <div
        className={clsx('tw-rounded-md tw-bg-slate-300/40 tw-p-2 tw-border-[1px] tw-flex tw-flex-col tw-gap-y-2', {
          'tw-border-red-500 tw-text-red-500': !!error,
          'tw-border-slate-300': !error,
        })}
      >
        {field.options?.map((option: string, index) => {
          return (
            <InputRadio
              key={index}
              value={option}
              label={option}
              checked={values[field.name as keyof typeof values] === option}
              onChange={onChange}
            />
          )
        })}
      </div>
      <Transition
        show={!!error}
        enter="tw-transition-opacity tw-duration-300"
        enterFrom="tw-opacity-0"
        enterTo="tw-opacity-100"
        leave="tw-transition-opacity tw-duration-300"
        leaveFrom="tw-opacity-100"
        leaveTo="tw-opacity-0"
        className={'tw-text-red-500 tw-text-xs'}
      >
        {t(error)}
      </Transition>
    </div>
  )
}
